import { useEffect, useRef, useState } from 'react';
import { produce } from 'immer';
import type { ReactNode } from 'react';
import { Form } from 'antd';
import dayjs from 'lib/dayjs';
import styled from '@emotion/styled';
import { useLatest, useSize } from 'ahooks';
import { PlusCircleFilled, CloseCircleFilled } from '@ant-design/icons';
import { v4 as uuidv4 } from 'uuid';
import { useTranslate } from '@tolgee/react';
import { DatePicker } from 'components/DatePicker';

type TimeGroupProps = {
  value?: Pick<TimeRangeItem, 'startAt' | 'endAt'>[];
  onChange: (value: Pick<TimeRangeItem, 'startAt' | 'endAt'>[]) => void;
};

const iconStyle = {
  color: 'var(--fett-text-tertiary)',
  cursor: 'pointer',
};

export type TimeRangeItem = {
  id: string;
  startAt: string | undefined;
  endAt: string | undefined;
  validateError: null | Error;
};

function newItem(defaultValue: boolean = false) {
  let startAt = undefined;
  let endAt = undefined;
  if (defaultValue) {
    const year = new Date().getFullYear();
    const month = new Date().getMonth() + 1;
    const day = new Date().getDate();
    startAt = `${year}-${month}-${day} 00:00:00`;
    endAt = `${year}-${month}-${day} 23:59:59`;
  }
  return {
    id: uuidv4(),
    startAt,
    endAt,
    validateError: null,
  };
}

function TimeGroup(props: TimeGroupProps) {
  const { value, onChange } = props;
  const onChangeRef = useLatest(onChange);
  const { t } = useTranslate();
  const [timeRangeItems, setTimeRangeItems] = useState<TimeRangeItem[]>(
    value?.length
      ? value.map((each) => {
          const year = new Date().getFullYear();
          const month = new Date().getMonth() + 1;
          const day = new Date().getDate();
          const { startAt, endAt } = each;
          return {
            ...each,
            id: uuidv4(),
            validateError: null,
            startAt: startAt ? `${year}-${month}-${day} ${startAt}` : undefined,
            endAt: endAt ? `${year}-${month}-${day} ${endAt}` : undefined,
          };
        })
      : [newItem(true)],
  );
  const timeRangeItemsRef = useLatest(timeRangeItems);

  const addTimeRangeItem = () => {
    if (timeRangeItems.length < 4) {
      setTimeRangeItems(
        produce((draft) => {
          draft.push(newItem());
        }),
      );
    }
  };

  const deleteTimeRangeItem = (id: string) => {
    setTimeRangeItems(
      produce((draft) => {
        const index = draft.findIndex((each) => each.id === id);
        if (index !== -1) {
          draft.splice(index, 1);
        }
      }),
    );
  };

  const onChangeIt = (id: string, values: any) => {
    if (values === null) {
      setTimeRangeItems(
        produce((draft) => {
          const index = draft.findIndex((each) => each.id === id);
          if (index !== -1) {
            // eslint-disable-next-line no-param-reassign
            draft[index].startAt = undefined;
            // eslint-disable-next-line no-param-reassign
            draft[index].endAt = undefined;
          }
        }),
      );
      return;
    }
    setTimeRangeItems(
      produce((draft) => {
        const index = draft.findIndex((each) => each.id === id);
        if (index !== -1) {
          draft[index].startAt = values[0];

          if (
            draft?.[index - 1]?.endAt &&
            dayjs(values[0]).isBefore(dayjs(draft[index - 1].endAt))
          ) {
            draft[index].validateError = new Error(t('multiTimeSelect.earlierTip'));
          } else {
            draft[index].validateError = null;
          }

          if (
            draft?.[index + 1]?.startAt &&
            dayjs(values[1]).isAfter(dayjs(draft[index + 1].startAt))
          ) {
            draft[index + 1].validateError = new Error(t('multiTimeSelect.earlierTip'));
          } else {
            draft?.[index + 1] && (draft[index + 1].validateError = null);
          }
          draft[index].endAt = values[1];
        }
      }),
    );
  };

  const validateEmpty = (id: string) => {
    setTimeRangeItems(
      produce((draft) => {
        const index = draft.findIndex((each) => each.id === id);
        const errorMsg = t('multiTimeSelect.请填写日期');
        if (index !== -1) {
          if (!draft[index].startAt || !draft[index].endAt) {
            draft[index].validateError = new Error(errorMsg);
          } else {
            if (draft[index].validateError?.message === errorMsg) {
              draft[index].validateError = null;
            }
          }
        }
      }),
    );
  };

  useEffect(() => {
    onChangeRef.current?.(
      timeRangeItems.map((each) => {
        const { startAt, endAt } = each;
        return {
          startAt: startAt ? dayjs(startAt).format('HH:mm') : undefined,
          endAt: endAt ? dayjs(endAt).format('HH:mm') : undefined,
        };
      }),
    );
  }, [timeRangeItems, onChangeRef]);

  return (
    <Form.Item label={t('multiTimeSelect.时间')} required>
      <Container>
        {timeRangeItems.map((each, index) => {
          return (
            <TimeItem key={each.id} validateError={each.validateError}>
              <Form.Item
                name={`i-dont-care-${each.id}`}
                validateTrigger={['onSubmit']}
                rules={[
                  {
                    validator: () => {
                      const value = timeRangeItemsRef.current.find((item) => item.id === each.id);
                      if (!value?.startAt || !value?.endAt) {
                        validateEmpty(each.id);
                        return Promise.reject();
                      } else {
                        if (value.validateError) {
                          return Promise.reject();
                        } else {
                          return Promise.resolve();
                        }
                      }
                    },
                  },
                ]}
              >
                <DatePicker.RangePicker
                  picker="time"
                  mode={undefined}
                  format="HH:mm"
                  defaultValue={each.startAt ? [dayjs(each.startAt), dayjs(each.endAt)] : undefined}
                  onChange={(values) => onChangeIt(each.id, values)}
                  onOpenChange={(status) => {
                    if (!status) {
                      validateEmpty(each.id);
                    }
                  }}
                />
              </Form.Item>
              {index === 0 ? (
                <PlusCircleFilled
                  title={
                    timeRangeItems.length >= 4
                      ? t('multiTimeSelect.最多可增加4个时间段')
                      : t('multiTimeSelect.新增时间')
                  }
                  onClick={addTimeRangeItem}
                  style={{
                    ...iconStyle,
                    color:
                      timeRangeItems.length >= 4
                        ? 'rgb(204, 207, 215)'
                        : 'var(--fett-text-tertiary)',
                    cursor: timeRangeItems.length >= 4 ? 'not-allowed' : 'pointer',
                  }}
                />
              ) : (
                <CloseCircleFilled onClick={() => deleteTimeRangeItem(each.id)} style={iconStyle} />
              )}
            </TimeItem>
          );
        })}
      </Container>
    </Form.Item>
  );
}

const Container = styled.div``;

const TimeItemImpl = styled.div<{ paddingRight: number; error: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
  .ant-form-item-with-help .ant-form-item-explain {
    min-height: 0;
    height: 0;
  }
  .ant-picker-range {
    margin-right: 8px;
    padding-right: ${(props) => props.paddingRight - 230}px;
    border-color: rgb(217, 217, 217) !important;
    ${(props) => (props.error ? 'border-color: red !important;' : '')}
    .ant-picker-suffix {
      position: absolute;
      right: 11px;
    }
  }
  .ant-picker-focused {
    ${(props) => (props.error ? 'border-color: red;box-shadow: none;' : '')}
  }
`;

function TimeItem(props: { children: ReactNode; validateError: Error | null }) {
  const { children, validateError } = props;
  const ref = useRef(null);
  const size = useSize(ref);
  const paddingRight = size?.width || 0;

  const error = validateError ? (
    <div style={{ color: 'red', margin: '-4px 0 4px 0' }}>{validateError.message}</div>
  ) : null;
  return (
    <>
      <TimeItemImpl error={!!validateError} ref={ref} paddingRight={paddingRight}>
        {children}
      </TimeItemImpl>
      {error}
    </>
  );
}

export default TimeGroup;
